import { api_fetch } from "./client"

// Types

export interface BulkDeductionResult {
  deduction_id: string
  invoice_number: string
  is_disputable: boolean
  dispute_amount: number | null
  confidence: number
  reasoning: string
  dispute_message: string | null
  evidence: string[]
  pdf_analysis: string | null
}

export interface BulkAnalysisResponse {
  deductions: BulkDeductionResult[]
  summary: string
  cross_deduction_findings: string | null
}

export interface BulkAnalysisConversation {
  id: string
  org_id: string
  user_id: string
  deduction_ids: string[]
  initial_prompt: string
  results: BulkAnalysisResponse | null
  created_at: string
  updated_at: string
}

export interface BulkAnalysisMessage {
  id: string
  conversation_id: string
  role: "user" | "assistant"
  content: string
  created_at: string
}

export interface BulkAnalysisHistoryResponse {
  conversations: BulkAnalysisConversation[]
}

// Request types

export interface StartBulkAnalysisRequest {
  deduction_ids: string[]
  prompt: string
  selected_calendars?: string[]
  manual_promotional_context?: string
}

export interface FollowUpRequest {
  content: string
}

// SSE streaming types

export interface BulkAnalysisProgressEvent {
  type: "progress"
  step: string
  message: string
  current?: number
  total?: number
}

export interface BulkAnalysisResultEvent {
  type: "result"
  conversation: BulkAnalysisConversation
}

export interface BulkAnalysisErrorEvent {
  type: "error"
  error: string
}

export type BulkAnalysisStreamEvent =
  | BulkAnalysisProgressEvent
  | BulkAnalysisResultEvent
  | BulkAnalysisErrorEvent

export interface BulkAnalysisStreamCallbacks {
  onProgress: (event: BulkAnalysisProgressEvent) => void
  onResult: (conversation: BulkAnalysisConversation) => void
  onError: (error: string) => void
}

// API functions

/**
 * Start a new bulk analysis conversation (non-streaming)
 */
export async function startBulkAnalysis(
  request: StartBulkAnalysisRequest
): Promise<BulkAnalysisConversation> {
  const result = await api_fetch<BulkAnalysisConversation>(
    "/deduction/bulk-triage",
    {
      method: "POST",
      body: request,
    }
  )
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to start bulk analysis: ${result.error.message}`)
  }
}

/**
 * Start a new bulk analysis conversation with SSE streaming
 * Returns an abort controller to cancel the stream
 */
export function startBulkAnalysisStream(
  request: StartBulkAnalysisRequest,
  callbacks: BulkAnalysisStreamCallbacks
): AbortController {
  const abortController = new AbortController()

  // Use fetch with streaming response
  fetch("/api/deduction/bulk-triage/stream", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    credentials: "same-origin",
    body: JSON.stringify(request),
    signal: abortController.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        const text = await response.text()
        let message = "Failed to start bulk analysis"
        try {
          const json = JSON.parse(text)
          message = json.message || message
        } catch {
          // ignore
        }
        callbacks.onError(message)
        return
      }

      const reader = response.body?.getReader()
      if (!reader) {
        callbacks.onError("No response body")
        return
      }

      const decoder = new TextDecoder()
      let buffer = ""

      while (true) {
        const { done, value } = await reader.read()

        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Process complete SSE events
        const lines = buffer.split("\n")
        buffer = lines.pop() || "" // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6)
            if (data.trim()) {
              try {
                const event = JSON.parse(data) as BulkAnalysisStreamEvent
                
                switch (event.type) {
                  case "progress":
                    callbacks.onProgress(event)
                    break
                  case "result":
                    callbacks.onResult(event.conversation)
                    break
                  case "error":
                    callbacks.onError(event.error)
                    break
                }
              } catch (e) {
                console.error("Failed to parse SSE event:", e, data)
              }
            }
          }
        }
      }
    })
    .catch((error) => {
      if (error.name === "AbortError") {
        // User cancelled, don't call onError
        return
      }
      callbacks.onError(error.message || "Network error")
    })

  return abortController
}

/**
 * Send a follow-up message in a bulk analysis conversation
 */
export async function sendBulkAnalysisFollowUp(
  conversationId: string,
  request: FollowUpRequest
): Promise<BulkAnalysisMessage> {
  const result = await api_fetch<BulkAnalysisMessage>(
    `/deduction/bulk-triage/${conversationId}/follow-up`,
    {
      method: "POST",
      body: request,
    }
  )
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to send follow-up: ${result.error.message}`)
  }
}

/**
 * Get bulk analysis history for the current org
 */
export async function getBulkAnalysisHistory(): Promise<BulkAnalysisConversation[]> {
  const result = await api_fetch<BulkAnalysisHistoryResponse>(
    "/deduction/bulk-analysis/history",
    {
      method: "GET",
    }
  )
  if (result.ok) {
    return result.value.data.conversations
  } else {
    throw new Error(`Failed to get bulk analysis history: ${result.error.message}`)
  }
}

/**
 * Get a specific bulk analysis conversation
 */
export async function getBulkAnalysisResult(
  conversationId: string
): Promise<BulkAnalysisConversation> {
  const result = await api_fetch<BulkAnalysisConversation>(
    `/deduction/bulk-analysis/${conversationId}`,
    {
      method: "GET",
    }
  )
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to get bulk analysis result: ${result.error.message}`)
  }
}

/**
 * Get all messages in a bulk analysis conversation
 */
export async function getBulkAnalysisMessages(
  conversationId: string
): Promise<BulkAnalysisMessage[]> {
  const result = await api_fetch<BulkAnalysisMessage[]>(
    `/deduction/bulk-analysis/${conversationId}/messages`,
    {
      method: "GET",
    }
  )
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to get bulk analysis messages: ${result.error.message}`)
  }
}

export interface CheckAlreadyAnalyzedRequest {
  deduction_ids: string[]
}

export interface CheckAlreadyAnalyzedResponse {
  already_analyzed: string[]
}

/**
 * Check if any of the provided deductions have already been analyzed
 */
export async function checkAlreadyAnalyzed(
  request: CheckAlreadyAnalyzedRequest
): Promise<CheckAlreadyAnalyzedResponse> {
  const result = await api_fetch<CheckAlreadyAnalyzedResponse>(
    "/deduction/bulk-triage/check-already-analyzed",
    {
      method: "POST",
      body: request,
    }
  )
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to check already analyzed: ${result.error.message}`)
  }
}
